import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
  useBlogPosts,
  useCreatePost,
  useUpdatePost,
  useDeletePost,
  usePublishPost,
  useUnpublishPost,
  useArchivePost,
} from '@/hooks/useBlog';
import { getDocEditUrl } from '@/services/blogService';
import type { BlogPost, PostStatus, CreatePostRequest, UpdatePostRequest } from '@/types/blog';
import { POST_STATUS_LABELS, POST_STATUS_COLORS, BLOG_CATEGORIES } from '@/types/blog';
import toast from 'react-hot-toast';

type FilterStatus = PostStatus | 'all';

// Internal form state (with tags as array for easier manipulation)
interface FormState {
  title: string;
  title_zh: string;
  summary: string;
  summary_zh: string;
  doc_id: string;
  doc_id_zh: string;
  cover_image: string;
  category: string;
  tags: string[];
  author: string;
}

// Blog Post Metadata Dialog
interface BlogMetadataDialogProps {
  isOpen: boolean;
  onClose: () => void;
  post?: BlogPost;
  onSave: (data: CreatePostRequest | UpdatePostRequest) => void;
  isSaving: boolean;
}

const BlogMetadataDialog: React.FC<BlogMetadataDialogProps> = ({
  isOpen,
  onClose,
  post,
  onSave,
  isSaving,
}) => {
  const { t } = useTranslation('blog');
  const isEditing = !!post;

  const [formData, setFormData] = useState<FormState>({
    title: post?.title || '',
    title_zh: post?.title_zh || '',
    summary: post?.summary || '',
    summary_zh: post?.summary_zh || '',
    doc_id: post?.doc_id || '',
    doc_id_zh: post?.doc_id_zh || '',
    cover_image: post?.cover_image || '',
    category: post?.category || 'news',
    tags: post?.tags || [],
    author: post?.author || '',
  });

  const [tagInput, setTagInput] = useState('');

  // Reset form when dialog opens/closes or post changes
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        title: post?.title || '',
        title_zh: post?.title_zh || '',
        summary: post?.summary || '',
        summary_zh: post?.summary_zh || '',
        doc_id: post?.doc_id || '',
        doc_id_zh: post?.doc_id_zh || '',
        cover_image: post?.cover_image || '',
        category: post?.category || 'news',
        tags: post?.tags || [],
        author: post?.author || '',
      });
      setTagInput('');
    }
  }, [isOpen, post]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error(t('validation.titleRequired', 'Title is required'));
      return;
    }
    // Convert tags array to comma-separated string for API
    const requestData: CreatePostRequest = {
      title: formData.title,
      title_zh: formData.title_zh || undefined,
      summary: formData.summary,
      summary_zh: formData.summary_zh || undefined,
      doc_id: formData.doc_id,
      doc_id_zh: formData.doc_id_zh || undefined,
      cover_image: formData.cover_image || undefined,
      category: formData.category,
      tags: formData.tags.length > 0 ? formData.tags.join(',') : undefined,
      author: formData.author || undefined,
    };
    onSave(requestData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? t('dialog.editPost', 'Edit Post') : t('dialog.createPost', 'Create Post')}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title (EN) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('fields.title', 'Title (English)')} *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Title (ZH) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('fields.titleZh', 'Title (Chinese)')}
            </label>
            <input
              type="text"
              name="title_zh"
              value={formData.title_zh}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Summary (EN) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('fields.summary', 'Summary (English)')}
            </label>
            <textarea
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Summary (ZH) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('fields.summaryZh', 'Summary (Chinese)')}
            </label>
            <textarea
              name="summary_zh"
              value={formData.summary_zh}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Google Doc IDs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('fields.docId', 'Google Doc ID (EN)')}
              </label>
              <input
                type="text"
                name="doc_id"
                value={formData.doc_id}
                onChange={handleChange}
                placeholder="1abc..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('fields.docIdZh', 'Google Doc ID (ZH)')}
              </label>
              <input
                type="text"
                name="doc_id_zh"
                value={formData.doc_id_zh}
                onChange={handleChange}
                placeholder="1xyz..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Cover Image URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('fields.coverImage', 'Cover Image URL')}
            </label>
            <input
              type="url"
              name="cover_image"
              value={formData.cover_image}
              onChange={handleChange}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category & Author */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('fields.category', 'Category')}
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {BLOG_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('fields.author', 'Author')}
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('fields.tags', 'Tags')}
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder={t('fields.tagsPlaceholder', 'Add tag and press Enter')}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                {t('actions.add', 'Add')}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('actions.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving
                ? t('actions.saving', 'Saving...')
                : isEditing
                  ? t('actions.save', 'Save')
                  : t('actions.create', 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge: React.FC<{ status: PostStatus }> = ({ status }) => {
  const colors = POST_STATUS_COLORS[status];
  const label = POST_STATUS_LABELS[status];

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors}`}>
      {label}
    </span>
  );
};

// Main Blog Management Component
const BlogManagement: React.FC = () => {
  const { t } = useTranslation('blog');
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | undefined>(undefined);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Fetch posts based on filter
  const { data: postsResponse, isLoading, error, refetch } = useBlogPosts(
    statusFilter === 'all' ? undefined : { status: statusFilter }
  );
  const posts = postsResponse?.posts || [];

  // Mutations
  const createPost = useCreatePost();
  const updatePost = useUpdatePost();
  const deletePost = useDeletePost();
  const publishPost = usePublishPost();
  const unpublishPost = useUnpublishPost();
  const archivePost = useArchivePost();

  // Wait for auth to load
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  const handleCreatePost = () => {
    setEditingPost(undefined);
    setIsDialogOpen(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setIsDialogOpen(true);
  };

  const handleSavePost = async (data: CreatePostRequest | UpdatePostRequest) => {
    try {
      if (editingPost) {
        await updatePost.mutateAsync({ id: editingPost.id, request: data as UpdatePostRequest });
        toast.success(t('toast.updateSuccess', 'Post updated successfully'));
      } else {
        await createPost.mutateAsync(data as CreatePostRequest);
        toast.success(t('toast.createSuccess', 'Post created successfully'));
      }
      setIsDialogOpen(false);
      setEditingPost(undefined);
    } catch (err: unknown) {
      toast.error(
        editingPost
          ? t('toast.updateError', 'Failed to update post')
          : t('toast.createError', 'Failed to create post')
      );
    }
  };

  const handleDeletePost = async (id: string) => {
    try {
      await deletePost.mutateAsync(id);
      toast.success(t('toast.deleteSuccess', 'Post deleted successfully'));
      setDeleteConfirm(null);
    } catch {
      toast.error(t('toast.deleteError', 'Failed to delete post'));
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await publishPost.mutateAsync(id);
      toast.success(t('toast.publishSuccess', 'Post published successfully'));
    } catch {
      toast.error(t('toast.publishError', 'Failed to publish post'));
    }
  };

  const handleUnpublish = async (id: string) => {
    try {
      await unpublishPost.mutateAsync(id);
      toast.success(t('toast.unpublishSuccess', 'Post unpublished successfully'));
    } catch {
      toast.error(t('toast.unpublishError', 'Failed to unpublish post'));
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archivePost.mutateAsync(id);
      toast.success(t('toast.archiveSuccess', 'Post archived successfully'));
    } catch {
      toast.error(t('toast.archiveError', 'Failed to archive post'));
    }
  };

  const statusFilters: { value: FilterStatus; label: string }[] = [
    { value: 'all', label: t('filters.all', 'All') },
    { value: 'draft', label: t('filters.draft', 'Drafts') },
    { value: 'published', label: t('filters.published', 'Published') },
    { value: 'archived', label: t('filters.archived', 'Archived') },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('title', 'Blog Management')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('subtitle', 'Manage your blog posts and stories')}
          </p>
        </div>
        <button
          onClick={handleCreatePost}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('actions.newPost', 'New Post')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === filter.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Posts List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <p className="text-red-500">{t('error.loadFailed', 'Failed to load posts')}</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-blue-600 hover:underline"
            >
              {t('actions.retry', 'Try again')}
            </button>
          </div>
        ) : posts.length === 0 ? (
          <div className="py-12 text-center">
            <svg
              className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
              />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">
              {t('empty.noPosts', 'No posts found')}
            </p>
            <button
              onClick={handleCreatePost}
              className="mt-4 text-blue-600 hover:underline"
            >
              {t('empty.createFirst', 'Create your first post')}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {posts.map((post) => (
              <div
                key={post.id}
                className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Cover Image Thumbnail */}
                  {post.cover_image && (
                    <div className="flex-shrink-0 w-full sm:w-24 h-32 sm:h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <StatusBadge status={post.status} />
                      <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded text-xs uppercase">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {post.title}
                    </h3>
                    {post.title_zh && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {post.title_zh}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                      {post.author && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {post.author}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
                    {/* Edit Doc Link */}
                    {post.doc_id && (
                      <a
                        href={getDocEditUrl(post.doc_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                        title={t('actions.editDoc', 'Edit Google Doc')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Doc
                      </a>
                    )}

                    {/* Edit Metadata */}
                    <button
                      onClick={() => handleEditPost(post)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      {t('actions.edit', 'Edit')}
                    </button>

                    {/* Status Actions */}
                    {post.status === 'draft' && (
                      <button
                        onClick={() => handlePublish(post.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t('actions.publish', 'Publish')}
                      </button>
                    )}

                    {post.status === 'published' && (
                      <button
                        onClick={() => handleUnpublish(post.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        {t('actions.unpublish', 'Unpublish')}
                      </button>
                    )}

                    {post.status !== 'archived' && (
                      <button
                        onClick={() => handleArchive(post.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        {t('actions.archive', 'Archive')}
                      </button>
                    )}

                    {/* Delete */}
                    <button
                      onClick={() => setDeleteConfirm(post.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      {t('actions.delete', 'Delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <BlogMetadataDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditingPost(undefined);
        }}
        post={editingPost}
        onSave={handleSavePost}
        isSaving={createPost.isLoading || updatePost.isLoading}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {t('dialog.confirmDelete', 'Confirm Delete')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('dialog.deleteWarning', 'Are you sure you want to delete this post? This action cannot be undone.')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('actions.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => handleDeletePost(deleteConfirm)}
                disabled={deletePost.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deletePost.isLoading ? t('actions.deleting', 'Deleting...') : t('actions.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
